import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { useState } from "react"
import { Link, useNavigate } from "react-router"
import { ArticleReviewInbox } from "./ArticleReviewInbox"
import { BatchCompanies } from "./BatchCompanies"
import { OperationsPanel } from "./OperationsPanel"
import { RecheckButton } from "./RecheckButton"
import { ReviewQueue } from "./ReviewQueue"
import { catalog, INDEX_PATH } from "./api"

export function CompanyIndexPage() {
  const enabled = useFeatureFlag("COMPANY_INDEX")
  const handoff = useFeatureFlag("COMPANY_INDEX_ARTICLE_HANDOFF")
  const { user, hasPermission } = useUser()
  const { translate: t } = useTranslation()
  const navigate = useNavigate()
  const cache = useQueryClient()
  const [showInbox, setShowInbox] = useState(false)
  const [showOperations, setShowOperations] = useState(false)
  const [showBatch, setShowBatch] = useState(false)
  const [showQueue, setShowQueue] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [search, setSearch] = useState("")
  const [query, setQuery] = useState("")
  const [name, setName] = useState("")
  const [website, setWebsite] = useState("")
  const companies = useInfiniteQuery({
    queryKey: ["company-index", user?.organization, "companies", query],
    queryFn: ({ pageParam }) => catalog.companies(query, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: enabled && !!user?.organization,
  })
  const create = useGuardedMutation(
    useMutation({
      mutationFn: () => catalog.create({ name: name.trim(), website }),
      onSuccess: (company) => {
        void cache.invalidateQueries({
          queryKey: ["company-index", user?.organization],
        })
        navigate(`${INDEX_PATH}/${company.id}`)
      },
    }),
    PERMISSIONS.MC_DECLARATIONS_UPLOAD,
  )
  if (!enabled) return null
  const items = companies.data?.pages.flatMap((page) => page.items) ?? []
  return (
    <div className="mx-auto space-y-7 max-w-7xl p-6 lg:p-10">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="mb-3 text-xs tracking-widest text-muted-foreground">
            {t("CI_WORKSPACE")}
          </p>
          <h1 className="text-2xl font-semibold">{t("CI_TITLE")}</h1>
          <p className="text-muted-foreground">{t("CI_INDEX_INTRO")}</p>
        </div>
        {hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD) && (
          <Button onClick={() => setShowCreate(!showCreate)}>
            {t("CI_ADD_COMPANY")}
          </Button>
        )}
      </div>
      {showCreate && hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD) && (
        <Card>
          <CardHeader>
            <CardTitle>{t("CI_CREATE")}</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="flex flex-wrap gap-3"
              onSubmit={(e) => {
                e.preventDefault()
                create.mutate()
              }}
            >
              <Input
                className="max-w-sm"
                aria-label={t("CI_NAME")}
                placeholder={t("CI_NAME")}
                required
                maxLength={200}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Input
                className="max-w-sm"
                type="url"
                aria-label={t("CI_WEBSITE")}
                placeholder="https://supplier.example"
                required
                maxLength={4096}
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
              <Button disabled={create.isPending || !name.trim()} type="submit">
                {t("CI_CREATE")}
              </Button>
            </form>
            {create.isError && (
              <p role="alert" className="mt-3 text-destructive">
                {t("CI_ERROR")}
              </p>
            )}
          </CardContent>
        </Card>
      )}
      {handoff &&
        hasPermission(PERMISSIONS.MC_DECLARATIONS_REVIEW) &&
        hasPermission(PERMISSIONS.MC_DECLARATIONS_VIEW) && (
          <div className="flex gap-2">
            <Button
              variant={showInbox ? "outline" : "default"}
              onClick={() => setShowInbox(false)}
            >
              {t("CI_REVIEW_COMPANIES")}
            </Button>
            <Button
              variant={showInbox ? "default" : "outline"}
              onClick={() => setShowInbox(true)}
            >
              {t("CI_REVIEW_INBOX")}
            </Button>
          </div>
        )}
      {showInbox ? (
        <ArticleReviewInbox />
      ) : (
        <>
          <RecheckButton />
          <Button
            variant="outline"
            aria-expanded={showQueue}
            onClick={() => setShowQueue(!showQueue)}
          >
            {t("CI_REVIEW_QUEUE")}
          </Button>
          {showQueue && <ReviewQueue />}
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              aria-expanded={showOperations}
              onClick={() => setShowOperations(!showOperations)}
            >
              {t("CI_OPERATIONS")}
            </Button>
            {hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD) && (
              <Button
                variant="outline"
                aria-expanded={showBatch}
                onClick={() => setShowBatch(!showBatch)}
              >
                {t("CI_BATCH_TITLE")}
              </Button>
            )}
          </div>
          {showOperations && <OperationsPanel />}
          {showBatch && <BatchCompanies />}
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              setQuery(search)
            }}
          >
            <Input
              aria-label={t("CI_SEARCH")}
              placeholder={t("CI_SEARCH")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              maxLength={200}
            />
            <Button type="submit">{t("CI_SEARCH")}</Button>
          </form>
          {companies.isPending && <p>{t("CI_LOADING")}</p>}
          {companies.isError && (
            <p role="alert">
              {t("CI_ERROR")}{" "}
              <Button
                variant="outline"
                onClick={() => void companies.refetch()}
              >
                {t("CI_RETRY")}
              </Button>
            </p>
          )}
          {companies.isSuccess && !items.length && (
            <p className="text-muted-foreground">{t("CI_EMPTY")}</p>
          )}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((company) => (
              <Link key={company.id} to={`${INDEX_PATH}/${company.id}`}>
                <Card className="h-full rounded-xl py-5 transition-colors hover:border-primary">
                  <CardHeader>
                    <CardTitle>{company.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm break-all text-muted-foreground">
                    {company.website}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          {companies.hasNextPage && (
            <Button
              variant="outline"
              disabled={companies.isFetchingNextPage}
              onClick={() => void companies.fetchNextPage()}
            >
              {t("CI_MORE")}
            </Button>
          )}
        </>
      )}
    </div>
  )
}
